import { useAppSelector } from '@repo/store/maps/hooks';
import { selectLayerVisibility, selectMapData } from '@repo/store/maps/slices/mapData';
import type { MapDataLabel, MapDataRegion } from '@repo/types/maps/map-types';
import { isMapReady } from '@repo/types/maps/map-utils';
import { getNumericId } from '@repo/utility/hash';
import type maplibregl from 'maplibre-gl';
import { useCallback, useEffect, useRef } from 'react';
import { useMapContext } from '../map-context';
function groupByZoom<T extends {
  properties?: {
    minZoom?: number;
    maxZoom?: number;
  };
}>(items: readonly T[]): Map<string, T[]> {
  const grouped = new Map<string, T[]>();
  items.forEach(item => {
    const minZoom = item.properties?.minZoom ?? 1;
    const maxZoom = item.properties?.maxZoom ?? 20;
    const key = `${minZoom}-${maxZoom}`;
    if (!grouped.has(key)) {
      grouped.set(key, []);
    }
    grouped.get(key)?.push(item);
  });
  return grouped;
}
function buildLabelLayer(layerId: string, sourceId: string, zoomKey: string): maplibregl.SymbolLayerSpecification {
  const [minZoom, maxZoom] = zoomKey.split('-').map(Number);
  return {
    id: layerId,
    type: 'symbol',
    source: sourceId,
    minzoom: minZoom,
    maxzoom: maxZoom,
    layout: {
      'text-field': ['coalesce', ['get', 'title'], ''],
      //changes to this font require conversion to pbf and placement in /public/maps/static/fonts/pbf/
      'text-font': ['GeneralSans-Semibold'],
      'text-size': ['coalesce', ['get', 'fontSize'], 18],
      'text-letter-spacing': 0.05,
      'text-anchor': 'center',
      'text-allow-overlap': true,
      'text-ignore-placement': true,
      'text-offset': [0, 1.5],
      'icon-image': ['get', 'iconId'],
      'icon-size': 1.0,
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
      'icon-anchor': 'center',
      'icon-offset': [0, -20]
    },
    paint: {
      'text-color': ['coalesce', ['get', 'color'], '#ffffff'],
      'text-opacity': 0.6,
      'text-halo-width': 0.7,
      'text-halo-color': '#000000',
      'text-halo-blur': 0.5,
      'icon-opacity': 0.8
    }
  };
}
export function useRegionLabelRenderer() {
  const {
    map
  } = useMapContext();
  const mapData = useAppSelector(selectMapData);
  const layerVisibility = useAppSelector(selectLayerVisibility);
  const isInitializedRef = useRef(false);
  const loadedIconsRef = useRef<Set<string>>(new Set());
  const hoveredRegionIdRef = useRef<string | number | null>(null);
  const regionsVisibleRef = useRef(layerVisibility.regions);
  useEffect(() => {
    regionsVisibleRef.current = layerVisibility.regions;
  }, [layerVisibility.regions]);
  const loadLabelIcons = useCallback(async (labels: readonly MapDataLabel[]) => {
    if (!isMapReady(map)) return;
    const iconsToLoad = labels.filter(l => {
      const hasIcon = l.properties?.iconId || l.properties?.iconPath;
      const iconKey = l.properties?.iconId ? String(l.properties.iconId) : l.properties?.iconPath;
      return hasIcon && !loadedIconsRef.current.has(iconKey || '');
    });
    if (iconsToLoad.length > 0) {
      await Promise.all(iconsToLoad.map(async label => {
        const iconId = label.properties?.iconId;
        const iconPath = label.properties?.iconPath;
        const imageUrl = iconId ? `${process.env.NEXT_PUBLIC_CDN_URL}/game_map_label_icon/${iconId}/50.webp` : iconPath;
        if (imageUrl) {
          try {
            const iconKey_0 = `label-icon-${label.properties.externalId}`;
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const imageBitmap = await createImageBitmap(blob);
            if (isMapReady(map) && !map.hasImage(iconKey_0)) {
              map.addImage(iconKey_0, imageBitmap);
              const trackingKey = iconId ? String(iconId) : iconPath;
              if (trackingKey) {
                loadedIconsRef.current.add(trackingKey);
              }
            }
          } catch (error) {
            console.error(`Failed to load icon for label ${label.properties?.externalId}:`, error);
          }
        }
      }));
    }
  }, [map]);
  const createRegionLayers = useCallback((sourceId: string, zoomKey: string, markerLayerId?: string) => {
    if (!map) return;
    const [minZoom, maxZoom] = zoomKey.split('-').map(Number);
    const fillLayerId = `region-fills-${zoomKey}`;
    const outlineLayerId = `region-outlines-${zoomKey}`;
    const visibility = regionsVisibleRef.current ? 'visible' : 'none';
    map.addLayer({
      id: fillLayerId,
      type: 'fill',
      source: sourceId,
      minzoom: minZoom,
      maxzoom: maxZoom,
      layout: {
        visibility
      },
      paint: {
        'fill-color': ['coalesce', ['get', 'color'], '#3b82f6'],
        'fill-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 0.2, 0]
      }
    }, markerLayerId);
    map.addLayer({
      id: outlineLayerId,
      type: 'line',
      source: sourceId,
      minzoom: minZoom,
      maxzoom: maxZoom,
      layout: {
        visibility
      },
      paint: {
        'line-color': ['coalesce', ['get', 'color'], '#3b82f6'],
        'line-width': ['coalesce', ['get', 'lineThickness'], 2],
        'line-opacity': 0.5
      }
    }, markerLayerId);
    map.on('mousemove', fillLayerId, e => {
      if (e.features && e.features.length > 0) {
        map.getCanvas().style.cursor = 'pointer';
        const feature = e.features[0];
        if (!feature) return;
        if (hoveredRegionIdRef.current !== null && hoveredRegionIdRef.current !== feature.id) {
          map.setFeatureState({
            source: sourceId,
            id: hoveredRegionIdRef.current
          }, {
            hover: false
          });
        }
        if (feature.id !== undefined && hoveredRegionIdRef.current !== feature.id) {
          hoveredRegionIdRef.current = feature.id;
          map.setFeatureState({
            source: sourceId,
            id: feature.id
          }, {
            hover: true
          });
        }
      }
    });
    map.on('mouseleave', fillLayerId, () => {
      map.getCanvas().style.cursor = '';
      if (hoveredRegionIdRef.current !== null) {
        map.setFeatureState({
          source: sourceId,
          id: hoveredRegionIdRef.current
        }, {
          hover: false
        });
        hoveredRegionIdRef.current = null;
      }
    });
  }, [map]);
  useEffect(() => {
    if (!map || !mapData) return;
    const setupRegionsAndLabels = async () => {
      const regions = mapData?.regions?.features || [];
      const labels_0 = mapData?.labels?.features || [];
      await loadLabelIcons(labels_0);
      if (!isMapReady(map)) return;
      const markerLayerId_0 = map.getLayer('markers') ? 'markers' : undefined;
      groupByZoom(regions).forEach((groupRegions, zoomKey_0) => {
        const sourceId_0 = `regions-${zoomKey_0}`;
        if (!map.getSource(sourceId_0)) {
          map.addSource(sourceId_0, {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: groupRegions.map((r, index) => ({
                type: 'Feature' as const,
                id: getNumericId(r.properties?.externalId, index + 1000000),
                properties: r.properties || {},
                geometry: r.geometry
                // biome-ignore lint/suspicious/noExplicitAny: update with maplibre typing in the future
              })) as any
            }
          });
          createRegionLayers(sourceId_0, zoomKey_0, markerLayerId_0);
        }
      });
      groupByZoom(labels_0).forEach((groupLabels, zoomKey_1) => {
        const sourceId_1 = `labels-${zoomKey_1}`;
        const layerId = `label-text-${zoomKey_1}`;
        if (!map.getSource(sourceId_1)) {
          map.addSource(sourceId_1, {
            type: 'geojson',
            data: {
              type: 'FeatureCollection',
              features: groupLabels.map(l_0 => ({
                ...l_0,
                type: 'Feature' as const,
                properties: {
                  ...l_0.properties,
                  iconId: l_0.properties?.iconId || l_0.properties?.iconPath ? `label-icon-${l_0.properties.externalId}` : null
                }
              }))
            }
          });
          map.addLayer(buildLabelLayer(layerId, sourceId_1, zoomKey_1), markerLayerId_0);
        }
      });
      isInitializedRef.current = true;
    };
    if (map.loaded()) {
      void setupRegionsAndLabels();
    } else {
      map.once('load', setupRegionsAndLabels);
    }
  }, [map, mapData, createRegionLayers, loadLabelIcons]);
  const updateRegions = useCallback((regions_0: readonly MapDataRegion[]) => {
    if (!map) return;
    const markerLayerId_1 = map.getLayer('markers') ? 'markers' : undefined;
    groupByZoom(regions_0).forEach((groupRegions_0, zoomKey_2) => {
      const sourceId_2 = `regions-${zoomKey_2}`;
      const source = map.getSource(sourceId_2) as maplibregl.GeoJSONSource;
      const features = groupRegions_0.map((r_0, index_0) => ({
        type: 'Feature' as const,
        id: getNumericId(r_0.properties?.externalId, index_0 + 1000000),
        properties: r_0.properties || {},
        geometry: r_0.geometry
        // biome-ignore lint/suspicious/noExplicitAny: update with maplibre typing in the future
      })) as any;
      if (source) {
        source.setData({
          type: 'FeatureCollection',
          features
        });
      } else {
        map.addSource(sourceId_2, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features
          }
        });
        createRegionLayers(sourceId_2, zoomKey_2, markerLayerId_1);
      }
    });
  }, [map, createRegionLayers]);
  const updateLabels = useCallback(async (labels_1: readonly MapDataLabel[]) => {
    await loadLabelIcons(labels_1);
    if (!isMapReady(map)) return;
    const markerLayerId_2 = map.getLayer('markers') ? 'markers' : undefined;
    groupByZoom(labels_1).forEach((groupLabels_0, zoomKey_3) => {
      const sourceId_3 = `labels-${zoomKey_3}`;
      const layerId_0 = `label-text-${zoomKey_3}`;
      const source_0 = map.getSource(sourceId_3) as maplibregl.GeoJSONSource;
      const features_0 = groupLabels_0.map(l_1 => ({
        ...l_1,
        type: 'Feature' as const,
        properties: {
          ...l_1.properties,
          iconId: l_1.properties?.iconId || l_1.properties?.iconPath ? `label-icon-${l_1.properties.externalId}` : null
        }
      }));
      if (source_0) {
        source_0.setData({
          type: 'FeatureCollection',
          features: features_0
        });
      } else {
        map.addSource(sourceId_3, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: features_0
          }
        });
        map.addLayer(buildLabelLayer(layerId_0, sourceId_3, zoomKey_3), markerLayerId_2);
      }
    });
  }, [map, loadLabelIcons]);
  useEffect(() => {
    if (!map || !mapData) return;
    if (!isInitializedRef.current) return;
    const updateRegionsAndLabels = () => {
      const regions_1 = mapData?.regions?.features || [];
      const labels_2 = mapData?.labels?.features || [];
      updateRegions([...regions_1]);
      void updateLabels([...labels_2]);
    };
    if (map.loaded()) {
      updateRegionsAndLabels();
    } else {
      map.once('load', updateRegionsAndLabels);
    }
  }, [mapData, map, updateRegions, updateLabels]);

  // Function to force refresh a specific label's icon
  const refreshLabelIcon = useCallback(async (labelId: string, iconId_0: number) => {
    if (!isMapReady(map)) return;
    try {
      const iconKey_1 = `label-icon-${labelId}`;
      const iconUrl = `${process.env.NEXT_PUBLIC_CDN_URL}/game_map_label_icon/${iconId_0}/50.webp`;
      if (map.hasImage(iconKey_1)) {
        map.removeImage(iconKey_1);
      }
      loadedIconsRef.current.delete(String(iconId_0));
      const response_0 = await fetch(iconUrl);
      const blob_0 = await response_0.blob();
      const imageBitmap_0 = await createImageBitmap(blob_0);
      if (!isMapReady(map)) return;
      map.addImage(iconKey_1, imageBitmap_0);
      loadedIconsRef.current.add(String(iconId_0));
      if (mapData?.labels?.features) {
        await updateLabels(mapData.labels.features);
      }
    } catch (error_0) {
      console.error(`Failed to refresh icon for label ${labelId}:`, error_0);
    }
  }, [map, mapData, updateLabels]);

  // Handle label visibility toggling
  useEffect(() => {
    if (!map || !mapData) return;
    if (!isInitializedRef.current) return;
    const labels_3 = mapData?.labels?.features || [];
    const visibility_0 = layerVisibility.labels ? 'visible' : 'none';
    groupByZoom(labels_3).forEach((_, zoomKey_4) => {
      const layerId_1 = `label-text-${zoomKey_4}`;
      if (map.getLayer(layerId_1)) {
        map.setLayoutProperty(layerId_1, 'visibility', visibility_0);
      }
    });
  }, [map, mapData, layerVisibility.labels]);

  // Handle region visibility toggling
  useEffect(() => {
    if (!map || !mapData) return;
    if (!isInitializedRef.current) return;
    const regions_2 = mapData?.regions?.features || [];
    const visibility_1 = layerVisibility.regions ? 'visible' : 'none';
    groupByZoom(regions_2).forEach((__0, zoomKey_5) => {
      const fillLayerId_0 = `region-fills-${zoomKey_5}`;
      const outlineLayerId_0 = `region-outlines-${zoomKey_5}`;
      if (map.getLayer(fillLayerId_0)) {
        map.setLayoutProperty(fillLayerId_0, 'visibility', visibility_1);
      }
      if (map.getLayer(outlineLayerId_0)) {
        map.setLayoutProperty(outlineLayerId_0, 'visibility', visibility_1);
      }
    });
  }, [map, mapData, layerVisibility.regions]);
  return {
    updateRegions,
    updateLabels,
    refreshLabelIcon
  };
}